import type { Command, CommandKey, CommandCategory } from "./types"
import { isValidCommandKey } from "./utils"

// ============================================================================
// COMMAND BUILDER PATTERN
// ============================================================================

export class CommandBuilder<TInput = any, TOutput = any> {
  private command: Partial<Command<TInput, TOutput>> = {}

  /**
   * Create a new CommandBuilder instance
   */
  static create<T = any, R = any>(key: string): CommandBuilder<T, R> {
    return new CommandBuilder<T, R>().key(key as CommandKey)
  }

  /**
   * Set the command key (required)
   */
  key(key: CommandKey): this {
    if (!isValidCommandKey(key)) {
      throw new Error("Command key must be a non-empty string")
    }
    this.command.key = key
    return this
  }

  /**
   * Set the command label (required)
   */
  label(label: string): this {
    if (!label || typeof label !== "string") {
      throw new Error("Command label must be a non-empty string")
    }
    this.command.label = label
    return this
  }

  /**
   * Set the command description
   */
  description(description: string): this {
    this.command.description = description
    return this
  }

  /**
   * Set the command category
   */
  category(category: CommandCategory): this {
    this.command.category = category
    return this
  }

  /**
   * Set the command owner
   */
  owner(owner: string): this {
    this.command.owner = owner
    return this
  }

  /**
   * Add tags to the command
   */
  tags(...tags: string[]): this {
    this.command.tags = [...(this.command.tags || []), ...tags]
    return this
  }

  /**
   * Set the command icon
   */
  icon(icon: string): this {
    this.command.icon = icon
    return this
  }

  /**
   * Set the keyboard shortcut
   */
  shortcut(shortcut: string): this {
    this.command.shortcut = shortcut
    return this
  }

  /**
   * Set the availability condition
   */
  when(condition: () => boolean | Promise<boolean>): this {
    this.command.when = condition
    return this
  }

  /**
   * Add search keywords
   */
  searchKeywords(...keywords: string[]): this {
    this.command.searchKeywords = [
      ...(this.command.searchKeywords || []),
      ...keywords,
    ]
    return this
  }

  /**
   * Set the command priority
   */
  priority(priority: number): this {
    this.command.priority = priority
    return this
  }

  /**
   * Set the command timeout
   */
  timeout(ms: number): this {
    if (ms <= 0) {
      throw new Error("Timeout must be a positive number")
    }
    this.command.timeout = ms
    return this
  }

  /**
   * Set the command handler (required)
   */
  handle(handler: (input?: TInput) => Promise<TOutput>): this {
    if (typeof handler !== "function") {
      throw new Error("Command handler must be a function")
    }
    this.command.handle = handler
    return this
  }

  /**
   * Build and return the command
   */
  build(): Command<TInput, TOutput> {
    if (!this.command.key) {
      throw new Error("Command key is required")
    }
    if (!this.command.label) {
      throw new Error("Command label is required")
    }
    if (!this.command.handle) {
      throw new Error("Command handle is required")
    }

    return this.command as Command<TInput, TOutput>
  }

  /**
   * Clone this builder to create a new one with the same configuration
   */
  clone(): CommandBuilder<TInput, TOutput> {
    const cloned = new CommandBuilder<TInput, TOutput>()
    cloned.command = { ...this.command }
    return cloned
  }

  /**
   * Reset the builder to start over
   */
  reset(): this {
    this.command = {}
    return this
  }

  /**
   * Get the current command state (for debugging)
   */
  getState(): Partial<Command<TInput, TOutput>> {
    return { ...this.command }
  }
}

// ============================================================================
// COMMAND TEMPLATES
// ============================================================================

export class CommandTemplate {
  /**
   * Create a system command template
   */
  static system(): CommandBuilder {
    return CommandBuilder.create("system:placeholder")
      .category("system")
      .owner("system")
      .priority(10)
  }

  /**
   * Create a file command template
   */
  static file(): CommandBuilder {
    return CommandBuilder.create("file:placeholder")
      .category("file")
      .tags("file")
      .priority(5)
  }

  /**
   * Create a debug command template
   */
  static debug(): CommandBuilder {
    return CommandBuilder.create("debug:placeholder")
      .category("debug")
      .owner("debug")
      .tags("debug", "development")
      .when(() => process.env.NODE_ENV === "development")
      .priority(15)
  }

  /**
   * Create a view command template
   */
  static view(): CommandBuilder {
    return CommandBuilder.create("view:placeholder")
      .category("view")
      .tags("view", "ui")
      .priority(3)
  }

  /**
   * Create a tools command template
   */
  static tools(): CommandBuilder {
    return CommandBuilder.create("tools:placeholder")
      .category("tools")
      .tags("tools", "utility")
      .priority(8)
  }

  /**
   * Create a custom command template
   */
  static custom(): CommandBuilder {
    return CommandBuilder.create("custom:placeholder")
      .category("custom")
      .owner("temporary")
      .priority(1)
  }
}

// ============================================================================
// FLUENT HELPERS
// ============================================================================

/**
 * Start building a command with a fluent API
 */
export function command<TInput = any, TOutput = any>(
  key: string,
): CommandBuilder<TInput, TOutput> {
  return CommandBuilder.create<TInput, TOutput>(key)
}

/**
 * Create a simple command quickly
 */
export function simpleCommand<TInput = any, TOutput = any>(
  key: string,
  label: string,
  handler: (input?: TInput) => Promise<TOutput>,
): Command<TInput, TOutput> {
  return CommandBuilder.create<TInput, TOutput>(key)
    .label(label)
    .handle(handler)
    .build()
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

export function validateCommandConfiguration(
  command: Partial<Command>,
): string[] {
  const errors: string[] = []

  if (!command.key) {
    errors.push("Command key is required")
  } else if (!isValidCommandKey(command.key)) {
    errors.push("Command key must be a non-empty string")
  }

  if (!command.label) {
    errors.push("Command label is required")
  } else if (
    typeof command.label !== "string" ||
    command.label.trim().length === 0
  ) {
    errors.push("Command label must be a non-empty string")
  }

  if (!command.handle) {
    errors.push("Command handle is required")
  } else if (typeof command.handle !== "function") {
    errors.push("Command handle must be a function")
  }

  if (
    command.timeout !== undefined &&
    (typeof command.timeout !== "number" || command.timeout <= 0)
  ) {
    errors.push("Command timeout must be a positive number")
  }

  if (command.priority !== undefined && typeof command.priority !== "number") {
    errors.push("Command priority must be a number")
  }

  return errors
}
